import clsx from 'clsx';
import styles from './base-input-wrapper.module.css';
import { Banner } from '@/components/banner';
import { MaterialSymbolIcon } from '../material-symbol-link';
import { ReactNode } from 'react';
import { FieldProps, useField } from '@/components/form';
import { ErrorField } from '../error-field';
import { InputLabel } from './input-label';
import { datesEqual, equalTexts } from '@/util/identity-fields';
import { FieldError } from 'react-hook-form';

export interface WarningOnChangeConfig {
  message: string;
  defaultValue: string;
}

function hasWarningOnChangeValueChanged(
  defaultValue: string,
  current?: string,
  inputType?: string,
): boolean {
  return inputType === 'date'
    ? !datesEqual(current, defaultValue)
    : !equalTexts(current, defaultValue);
}

interface BaseInputWrapperProps<T extends HTMLInputElement | HTMLTextAreaElement> {
  name: string;
  label?: ReactNode;
  subLabel?: ReactNode;
  className?: string;
  withIconsIndicators?: boolean;
  withErrorMessage?: boolean;
  trimWhiteSpaces?: boolean;
  roundNumbers?: boolean;
  description?: string;
  warningOnChange?: WarningOnChangeConfig;
  children: (props: {
    fieldProps: FieldProps<T>;
    hasError: boolean;
    error: FieldError | undefined;
    showSuccess: boolean;
    inputClassName: string;
    getValue: () => string | undefined;
    htmlFor: string;
  }) => ReactNode;
  additionalContainerClassName?: string;
  id?: string;
  readersOnlyLabel?: boolean;
  restProps?: React.InputHTMLAttributes<T> | React.TextareaHTMLAttributes<T>;
}

export function BaseInputWrapper<T extends HTMLInputElement | HTMLTextAreaElement>({
  name,
  label,
  subLabel,
  className,
  withIconsIndicators = true,
  withErrorMessage = true,
  trimWhiteSpaces = false,
  roundNumbers = false,
  description,
  warningOnChange,
  children,
  additionalContainerClassName,
  id,
  readersOnlyLabel = false,
  restProps = {},
}: BaseInputWrapperProps<T>) {
  const { fieldProps, fieldState, error, isSubmitted, getValue, setValue } = useField(
    name,
    restProps,
  );
  const hasError = !!error && (isSubmitted || !fieldState.isFocused);
  const showError = withErrorMessage && hasError;
  const showSuccess = fieldState.isDirty && fieldState.isTouched && !fieldState.isFocused && !error;

  const inputType = 'type' in restProps ? restProps.type : undefined;
  const hasChanged = warningOnChange
    ? hasWarningOnChangeValueChanged(warningOnChange.defaultValue, getValue(), inputType)
    : false;
  const warning =
    warningOnChange && (fieldState.isFocused || hasChanged) ? warningOnChange.message : undefined;

  const inputClassName = clsx(
    'body-2',
    styles.input,
    showError && styles.hasError,
    showSuccess && styles.hasSuccess,
  );

  const handleBlur = (e: React.FocusEvent<T>) => {
    if (trimWhiteSpaces && e.target.value) {
      const trimmedValue = e.target.value.trim();
      if (trimmedValue !== e.target.value) {
        setValue(trimmedValue);
      }
    }

    if (roundNumbers && e.target.value) {
      const roundedValue = Math.round(Number(e.target.value)).toString();
      if (roundedValue !== e.target.value) {
        setValue(roundedValue);
      }
    }
    fieldProps.onBlur(e);
  };

  const htmlFor = id ?? `form-field-${name}`;

  const wrappedFieldProps = {
    ...fieldProps,
    onBlur: handleBlur,
  };

  return (
    <div className={clsx(styles.textInput, className)}>
      {label && (
        <InputLabel
          label={label}
          subLabel={subLabel}
          required={fieldProps.required}
          htmlFor={htmlFor}
          readersOnly={readersOnlyLabel}
        />
      )}
      <div className={clsx(styles.inputContainer, additionalContainerClassName)}>
        {children({
          fieldProps: wrappedFieldProps,
          hasError,
          error,
          showSuccess,
          inputClassName,
          getValue,
          htmlFor,
        })}
        {withIconsIndicators && showError && (
          <MaterialSymbolIcon name="close" fill className={styles.errorIcon} />
        )}
        {withIconsIndicators && showSuccess && (
          <MaterialSymbolIcon name="check" fill className={styles.successIcon} />
        )}
      </div>
      {!showError && description && !warning && (
        <span className={clsx('caption', styles.description)}>{description}</span>
      )}
      {!showError && warning && (
        <Banner type="warning" label={warning} className={styles.warningBanner} />
      )}
      {showError && <ErrorField error={error} />}
    </div>
  );
}
